import { EventType, mergeConnectConfig } from '../lib';
import { BaseEvent, BaseExternalEvent, ConnectToAgent } from '../types';
import { MessageMetadata } from './ChatFramework.types';

export type PrePromptConditions = { hasAction?: boolean; hasSupport: boolean; hasMetadataRules?: boolean };
export const conditionHelper =
  ({ hasAction, hasSupport, hasMetadataRules }: PrePromptConditions) =>
  (source: PrePromptConditions) =>
    (hasAction === undefined || source.hasAction === hasAction) &&
    source.hasSupport === hasSupport &&
    (hasMetadataRules === undefined || source.hasMetadataRules === hasMetadataRules);

export function isPromise<T>(variable: any): variable is Promise<T> {
  return !!variable && typeof variable === 'object' && typeof variable.then === 'function';
}

export function toArr<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}
export const noop = () => void 0;

export function contains<T>(type: 'all' | 'any', haystack: Array<T>, ...check: Array<T>): boolean {
  const haystackSet = new Set(haystack); // Convert haystack to a Set for faster lookups

  if (type === 'all') {
    // Check if every element in check is in the haystackSet
    return check.every((element) => haystackSet.has(element));
  } else {
    // Check if any element in check is in the haystackSet
    return check.some((element) => haystackSet.has(element));
  }
}

export function metaFilter(tags?: MessageMetadata[]) {
  let result = !!tags?.length;
  return {
    any(...checks: MessageMetadata[]) {
      if (!result) return this;
      result = result && contains('any', tags || [], ...checks);
      return this;
    },
    all(...checks: MessageMetadata[]) {
      if (!result) return this;
      result = result && contains('all', tags || [], ...checks);
      return this;
    },
    get() {
      return result;
    },
  };
}

export function generateUpdatedEvent<T extends BaseEvent | BaseExternalEvent>(
  event: T,
  connectConfig: Partial<ConnectToAgent>
): T {
  const isExternalEvent = EventType.isExternalEvent(event);

  if (isExternalEvent) {
    return {
      ...event,
      payload: { ...event.payload, supportOptions: mergeConnectConfig(event.payload.supportOptions, connectConfig) },
    };
  }

  // Due to old typescript version we need to explicitly set type here
  const baseEvent = event as BaseEvent;

  return {
    ...event,
    message: {
      ...baseEvent.message,
      config: {
        ...baseEvent.message?.config,
        connect: mergeConnectConfig(baseEvent.message?.config?.connect!, connectConfig),
      },
    },
  };
}

export function getUserEventMessages(events: unknown[]): string {
  let msg = 'Events: ';

  events.forEach((event: any, index: number) => {
    msg += `${event.home} vs ${event.away} (${event.eventId})${index === events.length - 1 ? '' : ', '}`;
  });

  return msg;
}
